/**
 * ABC Supply ordering constants.
 *
 * Codes and service meanings verified against ABC Place Orders docs (2026-11-02).
 * Availability remains subject to the selected branch.
 */

import type { AppointmentTypeCode, DeliveryServiceCode } from '../../lib/distributorsApi';

export const ABC_MAX_ORDER_LINES = 99;   // hard API limit per order
export const ABC_MAX_QUOTE_LINES = 50;   // hard API limit per pricing call
export const PO_NUMBER_MAX = 16;         // purchaseOrder max length

export type DeliveryServiceOption = {
  value: DeliveryServiceCode;
  label: string;
  isPickup?: boolean;
};

export const DELIVERY_SERVICES: DeliveryServiceOption[] = [
  { value: 'COM', label: 'Common carrier' },
  { value: 'CPU', label: 'Customer pickup', isPickup: true },
  { value: 'EXP', label: 'Express pickup', isPickup: true },
  { value: 'OTR', label: 'Our truck — roof' },
  { value: 'OTG', label: 'Our truck — ground' },
  { value: 'OTW', label: 'Our truck — window' },
  { value: 'TPC', label: 'Third-party carrier' },
];

export const deliveryServiceLabel = (code?: string | null): string => {
  const match = DELIVERY_SERVICES.find((service) => service.value === code);
  return match ? match.label : (code || '');
};

export type AppointmentTypeOption = {
  value: AppointmentTypeCode;
  label: string;
  requiresFrom: boolean;
  requiresTo: boolean;
};

export const APPOINTMENT_TYPES: AppointmentTypeOption[] = [
  { value: 'AT', label: 'Anytime', requiresFrom: false, requiresTo: false },
  { value: 'AM', label: 'Morning', requiresFrom: false, requiresTo: false },
  { value: 'PM', label: 'Afternoon', requiresFrom: false, requiresTo: false },
  { value: 'FS', label: 'First stop', requiresFrom: false, requiresTo: false },
  { value: 'ST', label: 'Specific time', requiresFrom: true, requiresTo: false },
  { value: 'TR', label: 'Time range', requiresFrom: true, requiresTo: true },
];

export const appointmentTypeLabel = (code?: string | null): string => {
  const match = APPOINTMENT_TYPES.find((type) => type.value === code);
  return match ? match.label : (code || '');
};

/** Friendly text for OAuth return errors (?error=<code> on /operations/distributors). */
export const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'The ABC Supply sign-in was cancelled before finishing.',
  link_expired: 'The connection link expired — start the connection again.',
  invalid_state: 'The connection link was invalid — start the connection again.',
  missing_code: 'ABC Supply did not return an authorization code — try again.',
  no_integration: 'This distributor has no API integration enabled.',
  connection_failed: 'Connecting to ABC Supply failed — try again or contact support.',
};

export const oauthErrorMessage = (code: string): string =>
  OAUTH_ERROR_MESSAGES[code] || `Connecting the distributor account failed (${code}).`;
